import './App.css';
import React, { lazy, Suspense, useEffect } from 'react';
import { I18nextProvider, useTranslation, withTranslation } from 'react-i18next';
import i18n from './i18n';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';

import { APP_ROUTES } from './const/const';
import LoadingOverlay from './components/LoadingOverlay/LoadingOverlay';
import './assets/Stolzl-Book.ttf';
import './assets/TheSans-Plain-1.ttf';

// Lazy-loaded route components for better performance. The import thunks are
// named so the bootstrap can PRELOAD the active route's chunk before mounting
// the app — without that, a prerendered page rendered real content and THEN
// flashed the loading overlay while its own chunk downloaded (content →
// loading → content, backwards).
const pageChunks = {
  home: () => import('./components/Home/Home'),
  projects: () => import('./components/ProjectsPage/Projects'),
  forSale: () => import('./components/ForSalePage/ForSale'),
  about: () => import('./components/AboutUsPage/AboutUs'),
  services: () => import('./components/ServicesPage/Services'),
  contact: () => import('./components/ContactUsPage/ContactUs'),
  projectDetails: () => import('./components/ProjectDetailsPage/Main'),
  gallery: () => import('./components/GalleryPage/Gallery'),
  bookTour: () => import('./components/BookTourPage/BookTour'),
  floorPlans: () => import('./components/FloorPlansPage/FloorPlans'),
  bookingForm: () => import('./components/BookingForm/BookingForm'),
};

// preloadChunkForPath warms the chunk the given pathname will render. Never
// rejects: a failed preload just means Suspense shows its overlay as before.
export function preloadChunkForPath(pathname) {
  const p = (pathname || '/').replace(/^\/en(?=\/|$)/, '') || '/';
  const pick =
    p.startsWith(APP_ROUTES.PROJECT_DETAILS) ? pageChunks.projectDetails :
    p.startsWith(APP_ROUTES.PROJECT_GALLERY) ? pageChunks.gallery :
    p.startsWith(APP_ROUTES.PROJECT_FLOOR_PLANS) ? pageChunks.floorPlans :
    p.startsWith(APP_ROUTES.BOOK_A_TOUR) ? pageChunks.bookTour :
    p.startsWith(APP_ROUTES.BOOKING_FORM) ? pageChunks.bookingForm :
    p.startsWith(APP_ROUTES.FOR_SALE) ? pageChunks.forSale :
    p.startsWith(APP_ROUTES.PROJECTS) ? pageChunks.projects :
    p.startsWith(APP_ROUTES.ABOUT) ? pageChunks.about :
    p.startsWith(APP_ROUTES.SERVICES) ? pageChunks.services :
    p.startsWith(APP_ROUTES.CONTACT) ? pageChunks.contact :
    pageChunks.home;
  return pick().catch(() => {});
}

function RouteFallback() {
  const booting = Date.now() - (window.__drfahBootIntroAt || 0) < 6000;
  if (booting) {
    return <div className="app-boot-spinner" style={{ minHeight: '100vh', background: '#fff' }} />;
  }
  return <LoadingOverlay fullScreen bootGate />;
}

const Home = lazy(pageChunks.home);
const ProjectsPage = lazy(pageChunks.projects);
const ForSalePage = lazy(pageChunks.forSale);
const AboutUs = lazy(pageChunks.about);
const Services = lazy(pageChunks.services);
const ContactUs = lazy(pageChunks.contact);
const ProjectDetails = lazy(pageChunks.projectDetails);
const Gallery = lazy(pageChunks.gallery);
const BookTour = lazy(pageChunks.bookTour);
const FloorPlans = lazy(pageChunks.floorPlans);
const BookingForm = lazy(pageChunks.bookingForm);

/**
 * Handles /en/* routes: switches language to English,
 * then redirects to the equivalent default route (preserving query params).
 */
function EnglishRedirect() {
  const { '*': rest } = useParams();
  const location = useLocation();
  const { i18n: i18nInstance } = useTranslation();
  useEffect(() => { i18nInstance.changeLanguage('en'); }, [i18nInstance]);
  return <Navigate to={`/${rest}${location.search}`} replace />;
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>

      <div className="App container-lg">
        <ParallaxProvider>
          {/* Fallback for lazy route chunks — boot-aware: right after the
              boot splash/intro hands over, a SECOND loading visual reads as
              a collision, so during boot the fallback is a silent white
              frame (still carrying the app-boot-spinner readiness marker
              the prerender waits on). The branded blur overlay serves
              in-app route changes only. */}
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path={APP_ROUTES.HOME} element={<Home />} />
              <Route path={APP_ROUTES.PROJECTS} element={<ProjectsPage />} />
              <Route path={APP_ROUTES.FOR_SALE} element={<ForSalePage />} />
              <Route path={APP_ROUTES.ABOUT} element={<AboutUs />} />
              <Route path={APP_ROUTES.SERVICES} element={<Services />} />
              <Route path={APP_ROUTES.CONTACT} element={<ContactUs />} />
              {/* Canonical: path routing (/project-details/<slug>). The bare
                  routes stay so legacy ?project_id= links keep resolving
                  client-side (nginx also 301s them to the path form). */}
              <Route path={APP_ROUTES.PROJECT_DETAILS + '/:projectId'} element={<ProjectDetails />} />
              <Route path={APP_ROUTES.PROJECT_GALLERY + '/:projectId'} element={<Gallery />} />
              <Route path={APP_ROUTES.BOOK_A_TOUR + '/:projectId'} element={<BookTour />} />
              <Route path={APP_ROUTES.PROJECT_FLOOR_PLANS + '/:projectId'} element={<FloorPlans />} />
              <Route path={APP_ROUTES.PROJECT_DETAILS} element={<ProjectDetails />} />
              <Route path={APP_ROUTES.PROJECT_GALLERY} element={<Gallery />} />
              <Route path={APP_ROUTES.BOOK_A_TOUR} element={<BookTour />} />
              <Route path={APP_ROUTES.PROJECT_FLOOR_PLANS} element={<FloorPlans />} />
              <Route path={APP_ROUTES.BOOKING_FORM} element={<BookingForm />} />

              {/* English language prefix — switches to EN then redirects */}
              <Route path="/en/*" element={<EnglishRedirect />} />

              {/* Catch-all route for the home page */}
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </ParallaxProvider>
      </div>

    </I18nextProvider>
  );
}

export default withTranslation()(App);
